import React from 'react';
import classnames from 'classnames';
import { DynamicMediaPicture, MultiDynamicMediaImageConfigs, RichText } from '../index';
import { breakpoints } from '../../foundations';
import styles from './legal-disclaimer.module.scss';
import { ImageType, LinkType } from '../types';

export type ImageOrderType = 'leading' | 'trailing';

export type Size = 'small' | 'medium';

export interface LegalDisclaimerSingleType {
  legalText: string;
  className?: string;
  reference?: string;
  image?: ImageType;
  imgOrder?: ImageOrderType;
  size?: Size;
  imageLink?: LinkType;
}

const imageConfigs: MultiDynamicMediaImageConfigs = [
  {
    aspectRatio: 'none',
    sizes: '33vw',
    media: `(max-width: ${breakpoints.l / 16}em)`
  },
  {
    aspectRatio: 'none',
    sizes: '12vw',
    media: `(min-width: ${breakpoints.l / 16}em)`
  }
];

export const LegalDisclaimerSingle: React.FC<LegalDisclaimerSingleType> = ({
  legalText,
  image,
  className,
  reference,
  imageLink,
  imgOrder = 'trailing',
  size = 'small'
}) => {
  const renderRichText = () => (
    <RichText
      mdString={legalText}
      isLegal
      className={styles.richtext}
      customOverrides={size === 'medium' ? { p: { props: { className: styles.mediumSize } } } : {}}
    />
  );
  const generateDisclaimer = () =>
    reference ? (
      <dl className={styles.dl}>
        <dt className={`${styles.dt}${size === 'medium' ? ` ${styles.mediumSize}` : ``}`}>{reference}</dt>
        <dd className={styles.dd}>{renderRichText()}</dd>
      </dl>
    ) : (
      renderRichText()
    );

  const generateImage = (img: ImageType, isLeading = false) => {
    const classNames = classnames(styles.image, {
      [styles.leading]: isLeading,
      [styles.trailing]: !isLeading
    });

    const BasiceDynamicMediaPicture = () => (
      <DynamicMediaPicture
        className={!imageLink ? classNames : ``}
        imageAltText={img.imageAltText}
        imageUrl={img.imageUrl}
        imageConfigs={imageConfigs}
        hasImageTransparency={img.hasImageTransparency}
        maxImageHeight={640}
        omitAspectRatioInUrl
      />
    );

    return imageLink ? (
      <a className={classNames} {...imageLink}>
        <BasiceDynamicMediaPicture />
      </a>
    ) : (
      <BasiceDynamicMediaPicture />
    );
  };

  return (
    <div
      className={classnames(
        styles.legal,
        { [styles.withImage]: image, [styles.leading]: imgOrder === 'leading' },
        className
      )}
      data-testid="legaldisclaimer-component"
    >
      {image && imgOrder === 'leading' && generateImage(image, true)}
      {generateDisclaimer()}
      {image && generateImage(image)}
    </div>
  );
};
